import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Search } from 'lucide-react';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldSearch from '@/components/FormFieldSearch';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import { REPRESENTS } from '@/utils/constant';
import { clientSearch, clientContactSearch } from '@/services/Network';
import { clientClientIdGet } from '@brainvaulttech/dgr-react-sdk';
import { useQuery } from '@tanstack/react-query';
import { useClientBillingPreferenceGet } from '@/services/clients';
import { InvoiceType } from '@/types/accountsReceivable.types';
import CreateUpdateClientModal from '@pages/caseManagement/CreateUpdateClientModal';
import type {
  ClientGetAllParams,
  ClientResponse,
  ContactGetAllParams,
  ContactResponseType,
} from '@brainvaulttech/dgr-react-sdk';
import type { HIFormValues } from './hi.schema';

const SearchIcon = {
  Icon: Search,
  iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
};

export const HIClientDetails = () => {
  const { control, setValue, getValues, clearErrors } = useFormContext<HIFormValues>();
  const [clientId, setClientId] = useState<string | undefined>(getValues('clientId') || undefined);
  const [newClient, setNewClient] = useState<ClientResponse | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addNewClient, setAddNewClient] = useState(false);
  const [addNewContactFor, setAddNewContactFor] = useState<ContactResponseType | null>(null);

  const clientData = useQuery({
    queryFn: () => clientClientIdGet(clientId!),
    queryKey: ['client', clientId, refreshKey],
    enabled: !!clientId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: billingPreferenceData } = useClientBillingPreferenceGet(clientId);

  useEffect(() => {
    if (clientData?.data) {
      setValue('clientName', clientData.data.data.clientName, { shouldValidate: true });
    }
  }, [clientData?.data]);

  useEffect(() => {
    if (billingPreferenceData?.data?.billingType === InvoiceType.PREPAID) {
      setValue('prePaid', true);
    }
  }, [billingPreferenceData]);

  useEffect(() => {
    if (newClient) {
      resetClientDependents();
      setClientId(newClient.clientId);
      setValue('clientId', newClient.clientId, { shouldValidate: true });
      setValue('clientName', newClient.clientName);
      setRefreshKey((prev) => prev + 1);
    }
  }, [newClient]);

  const resetClientDependents = () => {
    setValue('clientName', null);
    setValue('contactId', '');
    setValue('contactEmail', null);
    setValue('lawyer', null);
    clearErrors(['clientName', 'contactId', 'contactEmail', 'lawyer']);
  };

  const handleSameDayRush = (checked: boolean) => {
    setValue('sameDayRushDisplay', checked);
    setValue('nextDayRushDisplay', false);
    setValue('rushType', checked ? 'SAME_DAY' : 'NONE');
    setValue('lastServeDate', checked ? new Date() : null);
  };

  const handleNextDayRush = (checked: boolean) => {
    setValue('nextDayRushDisplay', checked);
    setValue('sameDayRushDisplay', false);
    setValue('rushType', checked ? 'NEXT_DAY' : 'NONE');
    if (checked) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setValue('lastServeDate', tomorrow);
    } else {
      setValue('lastServeDate', null);
    }
  };

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      <div className="grid grid-cols-2 gap-6">
        <FormFieldComponent
          control={control}
          name="serviceType"
          label="Service Type"
          placeholder="Home Inspection"
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ClientGetAllParams>
          name="clientId"
          key={refreshKey}
          required
          label="Client #"
          placeholder="Search Client Code"
          props={SearchIcon}
          searchFieldName="code"
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, ...(newClient && { code: newClient.code }) };
            return clientSearch(updatedParams, options, refreshKey);
          }}
          onChange={(value) => {
            if (clientId && clientId === value) return;
            resetClientDependents();
            setClientId(value);
          }}
          addNewOption={{
            label: 'Add New Client',
            onNewOptionClick: () => setAddNewClient(true),
          }}
          onSearchChange={() => {
            setNewClient(undefined);
            setClientId(undefined);
            resetClientDependents();
          }}
        />
        <FormFieldComponent
          control={control}
          name="clientName"
          label="Client Name"
          placeholder="Client Name"
          required
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="contactId"
          placeholder="Search Client's Contact"
          label="Client's Contact"
          props={SearchIcon}
          required
          disabled={!clientId}
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId };
            return clientContactSearch(updatedParams, options, undefined, refreshKey);
          }}
          onChange={(_value, option) => {
            const contact = option as { email?: string; contactEmail?: string } | undefined;
            if (contact) {
              setValue('contactEmail', contact.email ?? contact.contactEmail ?? null);
            }
          }}
          {...(clientId && {
            addNewOption: {
              label: 'Add New Client Contact',
              onNewOptionClick: () => {
                setAddNewContactFor('STAFF' as ContactResponseType);
              },
            },
          })}
        />
        <FormFieldComponent
          control={control}
          name="contactEmail"
          label="Contact Email"
          placeholder="Contact Email"
          required
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent control={control} name="fileNumber" label="File #" placeholder="File #" />
        <FormFieldComponent control={control} name="caseNumber" label="Case #" placeholder="Case #" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="lawyer"
          placeholder="Search Attorney's Name"
          label="Attorney Name"
          props={SearchIcon}
          disabled={!clientId}
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId };
            return clientContactSearch(updatedParams, options, 'LAWYER' as ContactResponseType, refreshKey);
          }}
          {...(clientId && {
            addNewOption: {
              label: 'Add New Client Contact',
              onNewOptionClick: () => {
                setAddNewContactFor('LAWYER' as ContactResponseType);
              },
            },
          })}
        />
        <FormFieldSelect name="represents" options={REPRESENTS} placeholder="Select Represents" label="Represents" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent control={control} name="caseName" label="Case Name" placeholder="Case Name" />
      </div>

      <div className="flex flex-row gap-6 flex-wrap">
        <FormFieldCheckbox
          control={control}
          name="sameDayRushDisplay"
          label="Same Day Rush"
          onCheckChange={handleSameDayRush}
        />
        <FormFieldCheckbox
          control={control}
          name="nextDayRushDisplay"
          label="Next Day Rush"
          onCheckChange={handleNextDayRush}
        />
      </div>

      <div className="flex flex-row gap-6 flex-wrap">
        <FormFieldCheckbox control={control} name="prePaid" label="Pre-paid" />
      </div>

      {addNewClient && (
        <CreateUpdateClientModal
          onClose={() => setAddNewClient(false)}
          onUpdate={(client) => setNewClient(client)}
          operation="CREATE_CLIENT"
        />
      )}
      {addNewContactFor && (
        <CreateUpdateClientModal
          onClose={() => setAddNewContactFor(null)}
          onUpdate={(updatedClient) => {
            const newContact = updatedClient.contacts?.[0];
            if (newContact) {
              if (addNewContactFor === ('STAFF' as ContactResponseType)) {
                setValue('contactId', newContact.contactId ?? '');
                setValue('contactEmail', (newContact as { email?: string }).email ?? null);
              } else if (addNewContactFor === ('LAWYER' as ContactResponseType)) {
                setValue('lawyer', newContact.contactId ?? null);
              }
              clearErrors(['contactId', 'lawyer']);
            }
            setRefreshKey((prev) => prev + 1);
          }}
          operation="UPDATE_CONTACT"
          clientId={clientId}
        />
      )}
    </div>
  );
};
